import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { passwordResets } from '@/api/passwordResets';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const schema = z
  .object({
    password: z.string().min(12, 'Password must be at least 12 characters'),
    confirm: z.string().min(1, 'Please confirm your password'),
  })
  .refine((v) => v.password === v.confirm, {
    message: 'Passwords do not match',
    path: ['confirm'],
  });
type Values = z.infer<typeof schema>;

export function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [valid, setValid] = useState<null | boolean>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (!token) return;
    let ignore = false;
    passwordResets
      .lookup(token)
      .then(() => { if (!ignore) setValid(true); })
      .catch(() => { if (!ignore) setValid(false); });
    return () => { ignore = true; };
  }, [token]);

  async function onSubmit(values: Values) {
    setError(null);
    try {
      await passwordResets.complete(token, values.password);
      navigate('/login');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password.');
    }
  }

  if (!token) {
    return (
      <section className="mx-auto w-full max-w-md px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <Card>
          <CardHeader>
            <CardTitle>Set a new password</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-destructive">Missing token.</p>
          </CardContent>
        </Card>
      </section>
    );
  }

  if (valid === null) {
    return (
      <section className="mx-auto w-full max-w-md px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <Card>
          <CardHeader>
            <CardTitle>Set a new password</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Validating…</p>
          </CardContent>
        </Card>
      </section>
    );
  }

  if (valid === false) {
    return (
      <section className="mx-auto w-full max-w-md px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <Card>
          <CardHeader>
            <CardTitle>Set a new password</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-destructive">Reset link is invalid or expired.</p>
          </CardContent>
        </Card>
      </section>
    );
  }

  return (
    <section className="mx-auto w-full max-w-md px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
      <Card>
        <CardHeader>
          <CardTitle>Set a new password</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" noValidate onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-1">
              <Label htmlFor="password">New password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                aria-invalid={errors.password != null}
                aria-describedby={errors.password ? 'password-error' : undefined}
                {...register('password')}
              />
              {errors.password && (
                <p id="password-error" role="alert" className="text-xs text-destructive">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="confirm">Confirm new password</Label>
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                aria-invalid={errors.confirm != null}
                aria-describedby={errors.confirm ? 'confirm-error' : undefined}
                {...register('confirm')}
              />
              {errors.confirm && (
                <p id="confirm-error" role="alert" className="text-xs text-destructive">
                  {errors.confirm.message}
                </p>
              )}
            </div>

            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Saving…' : 'Save new password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </section>
  );
}
